import styled from "@emotion/styled";
import { FlexRow, H2, Link, HeadingGroup } from "atoms";
import { FollowButton } from "features/follows";
import { HomePageDataShape } from "features/hub/api/useHomePageData";
import { Thumbnail } from "features/images";

type FeaturedProfileProps = HomePageDataShape["featuredArtistBlock"];

function FeaturedProfileBlock({
  artist,
  largeImage,
  images,
}: FeaturedProfileProps) {
  const profileType = artist.type.toLowerCase();

  return (
    <FeaturedProfile>
      <FeaturedImageWrapper>
        <Thumbnail src={largeImage} alt={largeImage.altText || ""} />
      </FeaturedImageWrapper>

      <SHeadingGroup>
        <small>Featured {profileType}</small>
        <H2>
          <SLink to={`/${profileType}s/${artist.id}`}>
            {artist.type === "ARTIST" && "Meet"} {artist.name}
          </SLink>
        </H2>
      </SHeadingGroup>

      <ProfileGallery>
        {images.map((img) => (
          <Thumbnail key={img.url} src={img} alt={img.altText || ""} />
        ))}
      </ProfileGallery>

      <ProfileLinks>
        <FlexRow gap="10px">
          <FollowButton size="xxs" entityId={artist.id} />
          <Link
            variant="primary"
            size="xxs"
            arrow="r"
            to={`/${profileType}s/${artist.id}`}
          >
            View Profile
          </Link>
        </FlexRow>
      </ProfileLinks>
    </FeaturedProfile>
  );
}

const FeaturedProfile = styled.div(({ theme }) => ({
  width: "100%",
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: `10px ${theme.spacing.gap}`,
  gridTemplateAreas: `
  "profileImage"
  "text"
  "button"
  "gallery"`,
  [theme.breakpoints.tablet]: {
    gridTemplateColumns: "minmax(200px, 42%) 1fr",
    gridTemplateAreas: `
      "profileImage text"
      "profileImage button"
      "profileImage gallery"`,
  },
  [theme.breakpoints.desktop]: {
    gridTemplateColumns: "calc(100% / 3) 1fr",
    gridTemplateAreas: `
      "profileImage text"
      "profileImage button"
      "profileImage gallery"`,
  },
}));

const FeaturedImageWrapper = styled.section(({ theme }) => ({
  gridArea: "profileImage",
  borderRadius: theme.borderRadius.sm,
  overflow: "hidden",
  aspectRatio: "1",
  alignSelf: "end",
  "&>*": {
    aspectRatio: "1",
    width: "100%",
    maxWidth: "100%",
  },
  img: {
    borderRadius: theme.borderRadius.sm,
    display: "block",
    width: "100%",
  },
}));

const SHeadingGroup = styled(HeadingGroup)({
  gridArea: "text",
  alignSelf: "flex-end",
});

const ProfileGallery = styled(FlexRow)(({ theme }) => ({
  gridArea: "gallery",
  flexWrap: "nowrap",
  alignSelf: "end",
  gap: theme.spacing.gap,
  "&>*": {
    flex: "1 1 35%",
    aspectRatio: "1 / 1",
    overflow: "hidden",
  },
  img: {
    borderRadius: theme.borderRadius.sm,
    height: "100%",
    width: "100%",
    maxWidth: "100%",
    objectFit: "cover",
    objectPosition: "center",
  },
}));

const ProfileLinks = styled.div({
  gridArea: "button",
  maxWidth: "max-content",
  alignSelf: "center",
  "&& a": {
    textDecoration: "none",
  },
});

const SLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  "&&": {
    color: theme.colors.fg,
  },
}));

export { FeaturedProfileBlock };
